import { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { doDELETE, doPOST, doPUT } from '../util/HttpUtil';

const STATUS_RESET_MS = 3000;
const DEFAULT_DEBOUNCE_MS = 1500;
const DEFAULT_RECORD_KEY = 'primary';
// Retry is bounded per logical save plan so a bad payload cannot loop forever.
const MAX_RETRY_ATTEMPTS_PER_PLAN = 3;
const MAX_RETRY_DELAY_MS = 10000;
const MAX_FLUSH_WAIT_MS = 20000;
const FLUSH_POLL_INTERVAL_MS = 50;

const isDirtyUpdateError = (error) => {
    const rawMessage = String(error?.message || '');
    const message = rawMessage.toLowerCase();

    if (
        message.includes('dirty_update')
        || message.includes('already updated')
        || message.includes('trying to modify record which is already updated')
        || message.includes('kindly refresh your page and retry')
    ) {
        return true;
    }

    try {
        const parsed = JSON.parse(rawMessage);
        const parsedCode = String(parsed?.code || '').toLowerCase();
        const parsedMessage = String(parsed?.message || '').toLowerCase();
        return parsedCode === 'dirty_update'
            || parsedMessage.includes('already updated');
    } catch (parseError) {
        return false;
    }
};

const EMPTY_SAVE_ERROR_STATE = Object.freeze({ kind: null, error: null });

const classifyAutoSaveError = (error) => {
    if (isDirtyUpdateError(error)) {
        return 'dirty_update';
    }

    const rawMessage = String(error?.message || '');
    const message = rawMessage.toLowerCase();

    try {
        const parsed = JSON.parse(rawMessage);
        const parsedCode = String(parsed?.code || '').toLowerCase();
        const parsedMessage = String(parsed?.message || parsed?.customMessage || '').toLowerCase();

        if (parsedCode === 'dirty_update' || parsedMessage.includes('already updated')) {
            return 'dirty_update';
        }

        if (
            parsedCode === 'invalid_param'
            || parsedCode === 'validation'
            || parsedMessage.includes('invalid value')
            || parsedMessage.includes('invalid param')
            || parsedMessage.includes('expected ')
        ) {
            return 'validation';
        }
    } catch (parseError) {
        // Ignore parse errors and fall back to string heuristics below.
    }

    if (
        message.includes('invalid value')
        || message.includes('invalid param')
        || message.includes('expected ')
        || message.includes('validation')
    ) {
        return 'validation';
    }

    return 'request';
};

const formatPendingChangeValue = (value) => {
    if (value === undefined) {
        return 'removed';
    }

    if (value === null) {
        return 'cleared';
    }

    if (typeof value === 'string') {
        return value.length ? value : '""';
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    try {
        return JSON.stringify(value);
    } catch (error) {
        return String(value);
    }
};

const truncatePendingChangeValue = (value) => (
    value.length > 96 ? `${value.slice(0, 93)}...` : value
);

const formatPendingChangeLabel = (value) => {
    if (typeof value === 'string') {
        return value;
    }

    if (value === null || value === undefined) {
        return DEFAULT_RECORD_KEY;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    try {
        return JSON.stringify(value);
    } catch (error) {
        return String(value);
    }
};

const buildPendingChangesFromPlan = (plan) => {
    const operations = Array.isArray(plan?.operations) ? plan.operations : [];
    const pendingChanges = [];

    operations.forEach((operation, operationIndex) => {
        const recordKey = operation?.recordKey || DEFAULT_RECORD_KEY;
        const fieldValueMap = operation?.payload?.fieldValueMap;

        if (fieldValueMap && typeof fieldValueMap === 'object') {
            Object.entries(fieldValueMap).forEach(([path, value], fieldIndex) => {
                pendingChanges.push({
                    id: `${recordKey}:${operation.type || 'field'}:${path}:${operationIndex}:${fieldIndex}`,
                    label: formatPendingChangeLabel(path),
                    valuePreview: truncatePendingChangeValue(formatPendingChangeValue(value)),
                    operationType: operation?.type || 'field',
                    recordKey,
                });
            });
            return;
        }

        const arrayField = operation?.meta?.arrayField;
        const fallbackLabel = arrayField
            || operation?.label
            || `${recordKey}-change`;

        if (operation?.type === 'create') {
            pendingChanges.push({
                id: `${recordKey}:create:${fallbackLabel}:${operationIndex}`,
                label: formatPendingChangeLabel(fallbackLabel),
                valuePreview: 'new row pending',
                operationType: 'create',
                recordKey,
            });
            return;
        }

        if (operation?.type === 'delete') {
            pendingChanges.push({
                id: `${recordKey}:delete:${fallbackLabel}:${operationIndex}`,
                label: formatPendingChangeLabel(fallbackLabel),
                valuePreview: 'delete pending',
                operationType: 'delete',
                recordKey,
            });
            return;
        }

        if (operation?.type === 'full') {
            pendingChanges.push({
                id: `${recordKey}:full:${fallbackLabel}:${operationIndex}`,
                label: formatPendingChangeLabel(fallbackLabel),
                valuePreview: 'full document update',
                operationType: 'full',
                recordKey,
            });
        }
    });

    return _.uniqBy(pendingChanges, (change) => `${change.operationType}:${change.recordKey}:${change.label}:${change.valuePreview}`);
};

export default function useAutoSave({
    data,
    isEnabled = false,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    adapter,
    onDirtyUpdate,
    onDataReconcile,
    onSaveError,
    onFieldPersisted,
    onVersionIncrement,
}) {
    const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [pendingChangesCount, setPendingChangesCount] = useState(0);
    const [pendingChanges, setPendingChanges] = useState([]);
    const autoSaveStatusRef = useRef('idle');

    const dataRef = useRef(data);
    const snapshotRef = useRef(null);
    const isEnabledRef = useRef(isEnabled);
    const isPausedRef = useRef(false);
    const isSavingRef = useRef(false);
    const debounceTimerRef = useRef(null);
    const statusResetTimerRef = useRef(null);
    const saveTokenRef = useRef(0);
    const executeSaveRef = useRef(async () => false);
    // Versions are tracked by record alias plus _id because one screen can autosave
    // multiple documents with the same id namespace, for example primary + applicationForm.
    const knownVersionsRef = useRef(new Map());
    const queuedSaveRequestedRef = useRef(false);
    // Retry state is keyed by the logical save plan, not raw form state, so a dirty-update
    // refetch that only changes version does not reset the retry counter.
    const retryStateRef = useRef({ key: null, count: 0, blocked: false });
    const lastSaveErrorRef = useRef(EMPTY_SAVE_ERROR_STATE);
    const primaryRecordKey = adapter?.primaryRecordKey || DEFAULT_RECORD_KEY;

    // Keep refs aligned with the latest render immediately so a user can type and click
    // Next before effects run without flushing against stale pre-edit state.
    dataRef.current = data;
    isEnabledRef.current = isEnabled;

    useEffect(() => {
        autoSaveStatusRef.current = autoSaveStatus;
    }, [autoSaveStatus]);

    const getVersionCacheKey = useCallback((recordId, recordKey = DEFAULT_RECORD_KEY) => {
        if (!recordId) {
            return null;
        }
        return `${recordKey}::${recordId}`;
    }, []);

    const normalizeRefetchPayload = useCallback((payload) => {
        if (!payload || typeof payload !== 'object') {
            return { data: null, relatedRecords: [] };
        }

        if (Object.prototype.hasOwnProperty.call(payload, 'data') && payload?.data && typeof payload.data === 'object') {
            return {
                data: payload.data,
                relatedRecords: Array.isArray(payload?.relatedRecords) ? payload.relatedRecords : [],
            };
        }

        return {
            data: payload,
            relatedRecords: [],
        };
    }, []);

    const normalizeRelatedRecord = useCallback((recordEntry) => {
        if (!recordEntry || typeof recordEntry !== 'object') {
            return null;
        }

        if (recordEntry?.data && typeof recordEntry.data === 'object') {
            return {
                data: recordEntry.data,
                recordKey: recordEntry.recordKey || DEFAULT_RECORD_KEY,
            };
        }

        return {
            data: recordEntry,
            recordKey: recordEntry.recordKey || DEFAULT_RECORD_KEY,
        };
    }, []);

    const prepareData = useCallback((value) => {
        if (!value || typeof value !== 'object') return value;
        if (adapter?.prepareData) {
            return adapter.prepareData(value);
        }
        return _.cloneDeep(value);
    }, [adapter]);

    const getTrackedRecordsFromData = useCallback((sourceData) => {
        if (!sourceData || typeof sourceData !== 'object') {
            return [];
        }

        if (typeof adapter?.getTrackedRecords === 'function') {
            const trackedRecords = adapter.getTrackedRecords(sourceData);
            return Array.isArray(trackedRecords) ? trackedRecords : [];
        }

        return sourceData?._id
            ? [{
                recordKey: primaryRecordKey,
                data: sourceData,
            }]
            : [];
    }, [adapter, primaryRecordKey]);

    const getLatestPreparedData = useCallback(() => prepareData(dataRef.current), [prepareData]);

    const getLatestSavedSnapshot = useCallback(() => (
        snapshotRef.current && typeof snapshotRef.current === 'object'
            ? _.cloneDeep(snapshotRef.current)
            : snapshotRef.current
    ), []);

    const buildPlan = useCallback((snapshot, current) => {
        if (!adapter?.buildOperations || !current?._id) {
            return { operations: [], changeCount: 0 };
        }
        return adapter.buildOperations({ snapshot, current }) || { operations: [], changeCount: 0 };
    }, [adapter]);

    const extractPersistedFieldChanges = useCallback((operation) => {
        if (!operation || typeof operation !== 'object') {
            return [];
        }

        const recordKey = operation?.recordKey || DEFAULT_RECORD_KEY;
        const sourceFieldPathMap = operation?.meta?.sourceFieldPathMap || {};
        const sourceFieldIdMap = operation?.meta?.sourceFieldIdMap || {};
        const notifications = [];

        const appendNotification = (savePath, nextValue) => {
            if (!savePath) {
                return;
            }

            const mappedFieldPaths = Array.isArray(sourceFieldPathMap?.[savePath])
                ? sourceFieldPathMap[savePath]
                : (sourceFieldPathMap?.[savePath] ? [sourceFieldPathMap[savePath]] : []);
            const mappedFieldIds = Array.isArray(sourceFieldIdMap?.[savePath])
                ? sourceFieldIdMap[savePath]
                : (sourceFieldIdMap?.[savePath] ? [sourceFieldIdMap[savePath]] : []);
            const candidateFieldPaths = mappedFieldPaths.length ? mappedFieldPaths : [savePath];
            const fieldId = mappedFieldIds.find(Boolean) || undefined;

            candidateFieldPaths.forEach((fieldPath) => {
                if (!fieldPath) {
                    return;
                }

                notifications.push({
                    fieldPath,
                    fieldId,
                    nextValue: _.cloneDeep(nextValue),
                    savePath,
                    recordKey,
                    operation,
                });
            });
        };

        if (operation?.payload?.fieldValueMap && typeof operation.payload.fieldValueMap === 'object') {
            Object.entries(operation.payload.fieldValueMap).forEach(([savePath, nextValue]) => {
                appendNotification(savePath, nextValue);
            });
        } else if (operation?.type === 'full' && operation?.payload && typeof operation.payload === 'object') {
            Object.keys(sourceFieldPathMap).forEach((savePath) => {
                appendNotification(savePath, _.get(operation.payload, savePath));
            });
        }

        return _.uniqBy(
            notifications,
            (entry) => `${entry?.recordKey || DEFAULT_RECORD_KEY}:${entry?.savePath || ''}:${entry?.fieldPath || ''}:${entry?.fieldId || ''}`
        );
    }, []);

    const notifyPersistedFieldChanges = useCallback(async (operation) => {
        if (typeof onFieldPersisted !== 'function') {
            return;
        }

        const persistedFieldChanges = extractPersistedFieldChanges(operation);
        for (const fieldChange of persistedFieldChanges) {
            try {
                await onFieldPersisted(fieldChange);
            } catch (error) {
                // Review-task syncing should never fail the autosave that already persisted.
            }
        }
    }, [extractPersistedFieldChanges, onFieldPersisted]);

    const getCurrentPlanState = useCallback((sourceData = dataRef.current) => {
        const preparedCurrent = prepareData(sourceData);
        const snapshot = snapshotRef.current;

        if (!preparedCurrent?._id || !snapshot?._id || preparedCurrent._id !== snapshot._id) {
            return { preparedCurrent, snapshot, plan: { operations: [], changeCount: 0 } };
        }

        return {
            preparedCurrent,
            snapshot,
            plan: buildPlan(snapshot, preparedCurrent),
        };
    }, [buildPlan, prepareData]);

    const toVersion = useCallback((value) => {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : null;
    }, []);

    const rememberVersion = useCallback((recordId, version, recordKey = DEFAULT_RECORD_KEY) => {
        const cacheKey = getVersionCacheKey(recordId, recordKey);
        if (!cacheKey) {
            return null;
        }

        const nextVersion = toVersion(version);
        if (nextVersion === null) {
            return knownVersionsRef.current.has(cacheKey)
                ? knownVersionsRef.current.get(cacheKey)
                : null;
        }

        const currentVersion = knownVersionsRef.current.get(cacheKey);
        const resolvedVersion = currentVersion === undefined
            ? nextVersion
            : Math.max(currentVersion, nextVersion);

        knownVersionsRef.current.set(cacheKey, resolvedVersion);
        return resolvedVersion;
    }, [getVersionCacheKey, toVersion]);

    const rememberVersionFromRecord = useCallback((record, recordKey = DEFAULT_RECORD_KEY) => {
        if (!record || typeof record !== 'object' || !record?._id) {
            return null;
        }

        return rememberVersion(record._id, record.version, recordKey);
    }, [rememberVersion]);

    const syncKnownVersionsFromData = useCallback((sourceData) => {
        getTrackedRecordsFromData(sourceData).forEach((recordEntry) => {
            const trackedRecord = normalizeRelatedRecord(recordEntry);
            if (!trackedRecord?.data?._id) {
                return;
            }

            const syncedVersion = rememberVersionFromRecord(trackedRecord.data, trackedRecord.recordKey);
            if (syncedVersion !== null) {
                onVersionIncrement?.(syncedVersion, trackedRecord.data._id, trackedRecord.recordKey);
            }
        });
    }, [getTrackedRecordsFromData, normalizeRelatedRecord, onVersionIncrement, rememberVersionFromRecord]);

    const reconcileLiveData = useCallback((reconciler, operation) => {
        if (typeof reconciler !== 'function') {
            return;
        }

        const optimisticBaseData = dataRef.current;
        const optimisticNextData = reconciler(optimisticBaseData);

        if (optimisticNextData !== undefined) {
            dataRef.current = optimisticNextData;
        }

        if (typeof onDataReconcile === 'function') {
            onDataReconcile((currentData) => {
                const baseData = currentData && typeof currentData === 'object'
                    ? currentData
                    : optimisticBaseData;
                const resolvedNextData = reconciler(baseData);

                if (resolvedNextData !== undefined) {
                    dataRef.current = resolvedNextData;
                }

                return resolvedNextData;
            }, operation);
        }
    }, [onDataReconcile]);

    const forgetVersion = useCallback((recordId, recordKey = DEFAULT_RECORD_KEY) => {
        const cacheKey = getVersionCacheKey(recordId, recordKey);
        if (!cacheKey) {
            return;
        }

        knownVersionsRef.current.delete(cacheKey);
    }, [getVersionCacheKey]);

    const getKnownVersion = useCallback((recordId, fallbackVersion, recordKey = DEFAULT_RECORD_KEY) => {
        const cacheKey = getVersionCacheKey(recordId, recordKey);
        if (!recordId) {
            return toVersion(fallbackVersion) ?? 0;
        }

        if (cacheKey && knownVersionsRef.current.has(cacheKey)) {
            return knownVersionsRef.current.get(cacheKey);
        }

        if (recordKey === primaryRecordKey && snapshotRef.current?._id === recordId) {
            const snapshotVersion = rememberVersion(recordId, snapshotRef.current?.version, recordKey);
            if (snapshotVersion !== null) {
                return snapshotVersion;
            }
        }

        const fallbackNumericVersion = rememberVersion(recordId, fallbackVersion, recordKey);
        return fallbackNumericVersion ?? 0;
    }, [getVersionCacheKey, primaryRecordKey, rememberVersion, toVersion]);

    const getPlanRetryKey = useCallback((plan) => {
        const operations = Array.isArray(plan?.operations) ? plan.operations : [];
        if (!operations.length) {
            return null;
        }

        // Exclude version from the retry key so repeated optimistic-lock resyncs still count
        // as the same failing plan until the user actually changes the payload.
        return JSON.stringify(operations.map((operation) => {
            const payload = _.cloneDeep(operation?.payload);
            if (payload && typeof payload === 'object') {
                delete payload.version;
            }

            return {
                type: operation?.type,
                endpoint: operation?.endpoint,
                recordKey: operation?.recordKey || DEFAULT_RECORD_KEY,
                payload,
                meta: {
                    arrayField: operation?.meta?.arrayField || null,
                    recordId: operation?.meta?.recordId || null,
                    deletedId: operation?.meta?.deletedId || null,
                    matchLocalId: operation?.meta?.matchLocalId || null,
                    previousId: operation?.meta?.previousId || null,
                    insertIndex: Number.isInteger(operation?.meta?.insertIndex) ? operation.meta.insertIndex : null,
                    clearedFieldPaths: Array.isArray(operation?.meta?.clearedFieldPaths) ? operation.meta.clearedFieldPaths : [],
                },
            };
        }));
    }, []);

    const clearRetryState = useCallback(() => {
        retryStateRef.current = { key: null, count: 0, blocked: false };
    }, []);

    const clearLastSaveError = useCallback(() => {
        lastSaveErrorRef.current = EMPTY_SAVE_ERROR_STATE;
    }, []);

    const rememberLastSaveError = useCallback((error) => {
        const nextState = {
            kind: classifyAutoSaveError(error),
            error,
        };
        lastSaveErrorRef.current = nextState;
        return nextState;
    }, []);

    const getRetryStateForPlan = useCallback((plan) => {
        const planKey = getPlanRetryKey(plan);
        if (!planKey) {
            clearRetryState();
            return retryStateRef.current;
        }

        if (retryStateRef.current.key !== planKey) {
            retryStateRef.current = { key: planKey, count: 0, blocked: false };
        }

        return retryStateRef.current;
    }, [clearRetryState, getPlanRetryKey]);

    const registerRetryFailure = useCallback((plan) => {
        const planKey = getPlanRetryKey(plan);
        if (!planKey) {
            clearRetryState();
            return retryStateRef.current;
        }

        const nextCount = retryStateRef.current.key === planKey
            ? retryStateRef.current.count + 1
            : 1;
        const blocked = nextCount >= MAX_RETRY_ATTEMPTS_PER_PLAN;

        retryStateRef.current = {
            key: planKey,
            count: nextCount,
            blocked,
        };

        return retryStateRef.current;
    }, [clearRetryState, getPlanRetryKey]);

    const getRetryDelay = useCallback((attemptCount = 1) => (
        Math.min(debounceMs * Math.max(attemptCount, 1), MAX_RETRY_DELAY_MS)
    ), [debounceMs]);

    const clearDebounce = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, []);

    const clearStatusReset = useCallback(() => {
        if (statusResetTimerRef.current) {
            clearTimeout(statusResetTimerRef.current);
            statusResetTimerRef.current = null;
        }
    }, []);

    const scheduleStatusReset = useCallback(() => {
        clearStatusReset();
        statusResetTimerRef.current = setTimeout(() => {
            setAutoSaveStatus((currentStatus) => (currentStatus === 'saved' || currentStatus === 'error') ? 'idle' : currentStatus);
        }, STATUS_RESET_MS);
    }, [clearStatusReset]);

    const scheduleSave = useCallback((delay = debounceMs) => {
        clearDebounce();
        if (!isEnabledRef.current || isPausedRef.current) {
            return;
        }
        debounceTimerRef.current = setTimeout(() => {
            executeSaveRef.current();
        }, delay);
    }, [clearDebounce, debounceMs]);

    const syncAfterRefetch = useCallback((refetchPayload) => {
        const { data: freshData, relatedRecords } = normalizeRefetchPayload(refetchPayload);
        if (!freshData || typeof freshData !== 'object') {
            return;
        }

        // Dirty-update recovery refreshes the authoritative saved snapshot but intentionally
        // does not overwrite the user's in-progress draft in live React state.
        snapshotRef.current = prepareData(freshData);
        syncKnownVersionsFromData(snapshotRef.current);

        relatedRecords.forEach((recordEntry) => {
            const relatedRecord = normalizeRelatedRecord(recordEntry);
            if (!relatedRecord?.data?._id) {
                return;
            }

            const syncedVersion = rememberVersionFromRecord(relatedRecord.data, relatedRecord.recordKey);
            if (syncedVersion !== null) {
                onVersionIncrement?.(syncedVersion, relatedRecord.data._id, relatedRecord.recordKey);
            }
        });

        clearDebounce();
        setAutoSaveStatus('idle');
        clearLastSaveError();
    }, [
        clearDebounce,
        clearLastSaveError,
        normalizeRefetchPayload,
        normalizeRelatedRecord,
        onVersionIncrement,
        prepareData,
        rememberVersionFromRecord,
        syncKnownVersionsFromData,
    ]);

    const reconcilePendingState = useCallback((sourceData = dataRef.current, { reschedule = true } = {}) => {
        const preparedCurrent = prepareData(sourceData);
        const snapshot = snapshotRef.current;

        if (!preparedCurrent?._id || !snapshot?._id || preparedCurrent._id !== snapshot._id) {
            setPendingChangesCount(0);
            setPendingChanges([]);
            return { preparedCurrent, plan: { operations: [], changeCount: 0 } };
        }

        const plan = buildPlan(snapshot, preparedCurrent);
        const retryState = getRetryStateForPlan(plan);
        setPendingChangesCount(plan.changeCount || 0);
        setPendingChanges(buildPendingChangesFromPlan(plan));

        if (!isPausedRef.current && isEnabledRef.current) {
            if (plan.changeCount > 0 && reschedule) {
                if (retryState.blocked) {
                    clearDebounce();
                    setAutoSaveStatus((currentStatus) => (
                        currentStatus === 'saving' || currentStatus === 'resyncing'
                            ? currentStatus
                            : 'error'
                    ));
                } else if (isSavingRef.current) {
                    queuedSaveRequestedRef.current = true;
                } else {
                    scheduleSave();
                }
            } else if (plan.changeCount === 0 && !isSavingRef.current) {
                clearDebounce();
                setAutoSaveStatus((currentStatus) => currentStatus === 'saving' ? currentStatus : 'idle');
            }
        }

        return { preparedCurrent, plan };
    }, [buildPlan, clearDebounce, getRetryStateForPlan, prepareData, scheduleSave]);

    const executeSave = useCallback(async () => {
        if (isPausedRef.current || !isEnabledRef.current) {
            return false;
        }

        if (isSavingRef.current) {
            queuedSaveRequestedRef.current = true;
            return false;
        }

        const preparedCurrent = prepareData(dataRef.current);
        if (!preparedCurrent?._id) {
            setPendingChangesCount(0);
            setPendingChanges([]);
            setAutoSaveStatus('idle');
            return false;
        }

        if (!snapshotRef.current || snapshotRef.current?._id !== preparedCurrent._id) {
            snapshotRef.current = prepareData(dataRef.current);
            syncKnownVersionsFromData(snapshotRef.current);
            setPendingChangesCount(0);
            setPendingChanges([]);
            setAutoSaveStatus('idle');
            return false;
        }

        const plan = buildPlan(snapshotRef.current, preparedCurrent);
        if (!plan?.changeCount || !plan?.operations?.length) {
            clearRetryState();
            setPendingChangesCount(0);
            setPendingChanges([]);
            setAutoSaveStatus('idle');
            return true;
        }

        const retryState = getRetryStateForPlan(plan);
        if (retryState.blocked) {
            setAutoSaveStatus('error');
            return false;
        }

        const saveToken = saveTokenRef.current;
        isSavingRef.current = true;
        queuedSaveRequestedRef.current = false;
        clearDebounce();
        clearStatusReset();
        setPendingChangesCount(plan.changeCount);
        setPendingChanges(buildPendingChangesFromPlan(plan));
        setAutoSaveStatus('saving');

        let nextStatus = 'idle';
        let shouldResetStatus = false;
        let shouldScheduleFollowUp = false;
        let followUpDelay = debounceMs;
        let progressSnapshot = _.cloneDeep(snapshotRef.current);
        let hadSuccessfulOperations = false;

        try {
            for (const operation of plan.operations) {
                if (!operation?.endpoint || (operation?.type !== 'delete' && !operation?.payload)) {
                    continue;
                }

                const operationRecordKey = operation.recordKey || primaryRecordKey;
                let response = null;

                if (operation.type === 'create') {
                    response = await doPOST(operation.endpoint, _.cloneDeep(operation.payload));
                } else if (operation.type === 'delete') {
                    response = await doDELETE(operation.endpoint, _.cloneDeep(operation.payload));
                } else {
                    const operationPayload = _.cloneDeep(operation.payload);
                    if (operationPayload?._id && Object.prototype.hasOwnProperty.call(operationPayload, 'version')) {
                        operationPayload.version = getKnownVersion(operationPayload._id, operationPayload.version, operationRecordKey);
                    }
                    response = await doPUT(operation.endpoint, operationPayload);
                }

                if (response?.status !== 200) {
                    throw new Error(response?.data?.message || 'auto_save_failed');
                }

                if (operation.type === 'delete') {
                    const deletedId = operation?.meta?.deletedId || operation?.payload?._id || null;
                    if (typeof adapter?.applyOperationResult === 'function') {
                        progressSnapshot = adapter.applyOperationResult(progressSnapshot, operation, response?.data);
                        reconcileLiveData(
                            (currentData) => adapter.applyOperationResult(currentData, operation, response?.data),
                            operation
                        );
                    }
                    if (deletedId) {
                        forgetVersion(deletedId, operationRecordKey);
                    }
                    hadSuccessfulOperations = true;
                    continue;
                }

                const operationPayload = _.cloneDeep(operation.payload);
                const responseId = response?.data?._id || operationPayload?._id || operation?.meta?.recordId || null;
                const savedVersion = responseId
                    ? rememberVersion(responseId, response?.data?.version, operationRecordKey)
                    : null;

                if (typeof adapter?.applyOperationResult === 'function') {
                    // Structural autosave can create/update/delete child records, so the adapter
                    // reconciles backend ids/versions back into both the prepared snapshot and
                    // the live page data.
                    progressSnapshot = adapter.applyOperationResult(progressSnapshot, operation, response?.data);
                    reconcileLiveData(
                        (currentData) => adapter.applyOperationResult(currentData, operation, response?.data),
                        operation
                    );
                }

                if (
                    savedVersion !== null
                    && operation.type !== 'create'
                    && (responseId !== preparedCurrent?._id || operationRecordKey !== primaryRecordKey)
                ) {
                    onVersionIncrement?.(savedVersion, responseId, operationRecordKey);
                }

                await notifyPersistedFieldChanges(operation);
                hadSuccessfulOperations = true;
            }

            if (saveToken !== saveTokenRef.current) {
                return false;
            }

            const nextSnapshot = _.cloneDeep(progressSnapshot);
            const latestSavedVersion = getKnownVersion(nextSnapshot._id, nextSnapshot?.version, primaryRecordKey);
            const resolvedVersion = toVersion(latestSavedVersion);

            if (resolvedVersion !== null) {
                nextSnapshot.version = resolvedVersion;
                reconcileLiveData((currentData) => {
                    if (!currentData || typeof currentData !== 'object' || currentData?._id !== nextSnapshot?._id) {
                        return currentData;
                    }

                    const reconciledLiveData = {
                        ...currentData,
                        version: resolvedVersion,
                    };

                    return _.isEqual(reconciledLiveData, currentData)
                        ? currentData
                        : reconciledLiveData;
                }, {
                    type: 'version-sync',
                    recordKey: primaryRecordKey,
                });
                onVersionIncrement?.(resolvedVersion, nextSnapshot?._id, primaryRecordKey);
            }

            snapshotRef.current = nextSnapshot;
            syncKnownVersionsFromData(nextSnapshot);
            clearRetryState();
            clearLastSaveError();
            setLastSavedAt(new Date());

            const hadQueuedSaveRequest = queuedSaveRequestedRef.current;
            const nextState = reconcilePendingState(dataRef.current, { reschedule: false });
            shouldScheduleFollowUp = hadQueuedSaveRequest || (nextState?.plan?.changeCount > 0);
            followUpDelay = hadQueuedSaveRequest ? 0 : debounceMs;
            nextStatus = shouldScheduleFollowUp ? 'idle' : 'saved';
            shouldResetStatus = !shouldScheduleFollowUp;

            return true;
        } catch (error) {
            if (saveToken !== saveTokenRef.current) {
                return false;
            }

            const saveFailure = rememberLastSaveError(error);

            let retryPlan = plan;
            if (hadSuccessfulOperations && progressSnapshot?._id && progressSnapshot?._id === preparedCurrent?._id) {
                snapshotRef.current = _.cloneDeep(progressSnapshot);
                syncKnownVersionsFromData(snapshotRef.current);
                const remainingState = reconcilePendingState(dataRef.current, { reschedule: false });
                if (remainingState?.plan?.changeCount) {
                    retryPlan = remainingState.plan;
                } else {
                    clearRetryState();
                    return true;
                }
            }

            const nextRetryState = registerRetryFailure(retryPlan);

            if (saveFailure.kind === 'dirty_update') {
                setAutoSaveStatus('resyncing');
                try {
                    const freshData = await onDirtyUpdate?.(error);
                    if (freshData && saveToken === saveTokenRef.current) {
                        syncAfterRefetch(freshData);
                        const nextState = reconcilePendingState(dataRef.current, { reschedule: false });
                        const hasPendingChanges = (nextState?.plan?.changeCount || 0) > 0;
                        shouldScheduleFollowUp = hasPendingChanges && !nextRetryState.blocked;
                        followUpDelay = getRetryDelay(nextRetryState.count);
                        if (shouldScheduleFollowUp) {
                            nextStatus = 'idle';
                            shouldResetStatus = false;
                        } else if (hasPendingChanges) {
                            nextStatus = 'error';
                            shouldResetStatus = true;
                            onSaveError?.(error);
                        } else {
                            clearRetryState();
                            clearLastSaveError();
                            nextStatus = 'saved';
                            shouldResetStatus = true;
                        }
                    } else {
                        nextStatus = 'error';
                        onSaveError?.(error);
                        shouldResetStatus = true;
                        shouldScheduleFollowUp = !nextRetryState.blocked;
                        followUpDelay = getRetryDelay(nextRetryState.count);
                    }
                } catch (refetchError) {
                    nextStatus = 'error';
                    onSaveError?.(refetchError);
                    shouldScheduleFollowUp = !nextRetryState.blocked;
                    followUpDelay = getRetryDelay(nextRetryState.count);
                    shouldResetStatus = true;
                }
            } else if (saveFailure.kind === 'validation') {
                nextStatus = 'error';
                onSaveError?.(error);
                shouldResetStatus = true;
                shouldScheduleFollowUp = false;
            } else {
                nextStatus = 'error';
                onSaveError?.(error);
                shouldResetStatus = true;
                shouldScheduleFollowUp = !nextRetryState.blocked;
                followUpDelay = getRetryDelay(nextRetryState.count);
            }

            return false;
        } finally {
            if (saveToken === saveTokenRef.current) {
                isSavingRef.current = false;
                if (nextStatus === 'error') {
                    setAutoSaveStatus('error');
                } else if (nextStatus === 'saved') {
                    setAutoSaveStatus('saved');
                } else {
                    setAutoSaveStatus('idle');
                }

                if (shouldResetStatus && nextStatus === 'saved') {
                    scheduleStatusReset();
                }

                if (shouldScheduleFollowUp && !isPausedRef.current && isEnabledRef.current) {
                    scheduleSave(followUpDelay);
                }
            }
        }
    }, [
        adapter,
        buildPlan,
        clearDebounce,
        clearStatusReset,
        debounceMs,
        forgetVersion,
        getKnownVersion,
        onDirtyUpdate,
        onSaveError,
        onVersionIncrement,
        prepareData,
        primaryRecordKey,
        clearLastSaveError,
        clearRetryState,
        reconcilePendingState,
        getRetryDelay,
        getRetryStateForPlan,
        rememberLastSaveError,
        rememberVersion,
        registerRetryFailure,
        notifyPersistedFieldChanges,
        reconcileLiveData,
        scheduleSave,
        scheduleStatusReset,
        syncKnownVersionsFromData,
        syncAfterRefetch,
        toVersion,
    ]);

    useEffect(() => {
        executeSaveRef.current = executeSave;
    }, [executeSave]);

    const pauseAutoSave = useCallback(() => {
        saveTokenRef.current += 1;
        isPausedRef.current = true;
        isSavingRef.current = false;
        clearRetryState();
        clearLastSaveError();
        clearDebounce();
        clearStatusReset();
        setAutoSaveStatus('idle');
    }, [clearDebounce, clearLastSaveError, clearRetryState, clearStatusReset]);

    const resumeAutoSave = useCallback((savedData) => {
        if (savedData && typeof savedData === 'object') {
            snapshotRef.current = prepareData(savedData);
            syncKnownVersionsFromData(snapshotRef.current);
        }
        isPausedRef.current = false;
        isSavingRef.current = false;
        queuedSaveRequestedRef.current = false;
        clearRetryState();
        clearLastSaveError();
        clearDebounce();
        setAutoSaveStatus('idle');
        reconcilePendingState(dataRef.current, { reschedule: true });
    }, [clearDebounce, clearLastSaveError, clearRetryState, prepareData, reconcilePendingState, syncKnownVersionsFromData]);

    const flushNow = useCallback(async () => {
        if (isPausedRef.current || !isEnabledRef.current) {
            return true;
        }

        clearDebounce();
        const deadline = Date.now() + MAX_FLUSH_WAIT_MS;

        while (Date.now() < deadline) {
            const { preparedCurrent, snapshot, plan } = getCurrentPlanState();
            const hasPendingPlan = !!plan?.changeCount && !!plan?.operations?.length;

            if (!preparedCurrent?._id || !snapshot?._id || preparedCurrent._id !== snapshot._id) {
                return true;
            }

            if (!isSavingRef.current && !hasPendingPlan) {
                return autoSaveStatusRef.current !== 'error';
            }

            if (autoSaveStatusRef.current === 'error' && !isSavingRef.current) {
                return false;
            }

            if (isSavingRef.current) {
                await new Promise((resolve) => setTimeout(resolve, FLUSH_POLL_INTERVAL_MS));
                continue;
            }

            const saveResult = await executeSaveRef.current();

            if (!saveResult && !isSavingRef.current && lastSaveErrorRef.current?.kind) {
                return false;
            }

            await new Promise((resolve) => setTimeout(resolve, 0));
        }

        return false;
    }, [clearDebounce, getCurrentPlanState]);

    const flushAndPauseAutoSave = useCallback(async () => {
        const flushed = await flushNow();
        const latestData = getLatestPreparedData();
        const latestSnapshot = getLatestSavedSnapshot();
        if (!flushed) {
            const lastFailureKind = lastSaveErrorRef.current?.kind;
            const canBypassTerminalAutoSaveError = (
                !isSavingRef.current
                && autoSaveStatusRef.current === 'error'
                && lastFailureKind === 'request'
            );

            if (canBypassTerminalAutoSaveError) {
                pauseAutoSave();
                return {
                    ok: true,
                    latestData: getLatestPreparedData(),
                    latestSnapshot: getLatestSavedSnapshot(),
                    bypassedAutoSaveError: true,
                };
            }

            return {
                ok: false,
                latestData,
                latestSnapshot,
                reason: lastFailureKind === 'dirty_update'
                    ? 'out_of_date'
                    : (lastFailureKind === 'validation'
                        ? 'validation'
                        : ((isSavingRef.current || autoSaveStatusRef.current === 'saving' || autoSaveStatusRef.current === 'resyncing')
                            ? 'saving'
                            : 'save_failed')),
            };
        }

        pauseAutoSave();
        return {
            ok: true,
            latestData: getLatestPreparedData(),
            latestSnapshot: getLatestSavedSnapshot(),
        };
    }, [flushNow, getLatestPreparedData, getLatestSavedSnapshot, pauseAutoSave]);

    useEffect(() => {
        dataRef.current = data;
        if (data?._id) {
            syncKnownVersionsFromData(data);
        }
    }, [data, syncKnownVersionsFromData]);

    useEffect(() => {
        isEnabledRef.current = isEnabled;
        if (!isEnabled) {
            clearRetryState();
            clearDebounce();
            if (!isSavingRef.current) {
                setAutoSaveStatus('idle');
            }
            return;
        }
        reconcilePendingState(dataRef.current, { reschedule: true });
    }, [clearDebounce, clearRetryState, isEnabled, reconcilePendingState]);

    useEffect(() => {
        if (!data?._id) {
            snapshotRef.current = null;
            clearRetryState();
            setPendingChangesCount(0);
            setPendingChanges([]);
            return;
        }

        const preparedCurrent = prepareData(data);
        const currentSnapshot = snapshotRef.current;

        if (!currentSnapshot || currentSnapshot?._id !== preparedCurrent._id) {
            snapshotRef.current = preparedCurrent;
            syncKnownVersionsFromData(preparedCurrent);
            clearRetryState();
            clearLastSaveError();
            setPendingChangesCount(0);
            setPendingChanges([]);
            setAutoSaveStatus('idle');
            return;
        }

        reconcilePendingState(data, { reschedule: true });
    }, [clearLastSaveError, clearRetryState, data, prepareData, reconcilePendingState, syncKnownVersionsFromData]);

    useEffect(() => () => {
        clearDebounce();
        clearStatusReset();
    }, [clearDebounce, clearStatusReset]);

    const hasUnsavedChanges = pendingChangesCount > 0
        || autoSaveStatus === 'saving'
        || autoSaveStatus === 'resyncing'
        || autoSaveStatus === 'error';
    const isAutoSaveNavigationPending = isEnabled
        && !isPausedRef.current
        && autoSaveStatus !== 'error'
        && (
            pendingChangesCount > 0
            || autoSaveStatus === 'saving'
            || autoSaveStatus === 'resyncing'
        );

    return {
        autoSaveStatus,
        lastSavedAt,
        pendingChangesCount,
        pendingChanges,
        hasUnsavedChanges,
        isAutoSaveNavigationPending,
        pauseAutoSave,
        resumeAutoSave,
        syncAfterRefetch,
        flushNow,
        flushAndPauseAutoSave,
    };
}
